import { ArrowRight, ArrowRightLeft, Loader2, MapPin, Plus, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import LocationSelector, { type LocationDetails } from '../LocationSelector';
import { useAssignLocations, useRemoveUserLocation } from '../../hooks/useUsers';
import type { UserDto } from '../../types/api';
import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';

const EMPTY_LOCATION: LocationDetails = {
  provinceId: null,
  districtId: null,
  sectorId: null,
  cellId: null,
  villageId: null,
};

interface TransferUserLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserDto;
}

export const TransferUserLocationDialog: React.FC<TransferUserLocationDialogProps> = ({
  open,
  onOpenChange,
  user,
}) => {
  const [locationIds, setLocationIds] = useState<{ id: string; name: string }[]>(
    user.assignedLocations?.map(l => ({ id: l.geoLocation.id, name: l.geoLocation.name })) ?? []
  );
  const [pickerLocation, setPickerLocation] = useState<LocationDetails>(EMPTY_LOCATION);
  const [deepestLocation, setDeepestLocation] = useState<{ id: string; name: string } | null>(null);

  const removeLocationMutation = useRemoveUserLocation();
  const assignLocationsMutation = useAssignLocations();

  const isBusy = removeLocationMutation.isPending || assignLocationsMutation.isPending;

  useEffect(() => {
    if (open) {
      setLocationIds(
        user.assignedLocations?.map(l => ({ id: l.geoLocation.id, name: l.geoLocation.name })) ?? []
      );
      setPickerLocation(EMPTY_LOCATION);
      setDeepestLocation(null);
    }
  }, [open, user]);

  const resetPicker = () => {
    setPickerLocation(EMPTY_LOCATION);
    setDeepestLocation(null);
  };

  // Transfer: assignLocations REPLACES all assignments atomically, so a single
  // call both removes the old location and sets the new one. (A prior
  // remove-then-assign sequence left the user location-less mid-flight, which
  // failed the backend jurisdiction check for non-central admins.)
  const handleTransfer = async () => {
    if (!deepestLocation || locationIds.length !== 1) return;
    try {
      await assignLocationsMutation.mutateAsync({ id: user.id, data: { geoLocationIds: [deepestLocation.id] } });
      onOpenChange(false);
    } catch {
      // errors surfaced by hooks
    }
  };

  // Assign a new location (any case — adds to current list)
  const handleAssign = async () => {
    if (!deepestLocation || locationIds.some(l => l.id === deepestLocation.id)) return;
    try {
      await assignLocationsMutation.mutateAsync({
        id: user.id,
        data: { geoLocationIds: [...locationIds.map(l => l.id), deepestLocation.id] },
      });
      setLocationIds(prev => [...prev, deepestLocation]);
      resetPicker();
      if (locationIds.length === 0) onOpenChange(false); // central → assigned: close
    } catch {
      // errors surfaced by hooks
    }
  };

  // Remove a specific location
  const handleRemove = async (locId: string) => {
    try {
      await removeLocationMutation.mutateAsync({ userId: user.id, geoLocationId: locId });
      setLocationIds(prev => prev.filter(l => l.id !== locId));
    } catch {
      // errors surfaced by hooks
    }
  };

  const isCentral = locationIds.length === 0;
  const isSingle = locationIds.length === 1;

  return (
    <Dialog open={open} onOpenChange={v => !isBusy && onOpenChange(v)}>
      <DialogContent showCloseButton={!isBusy} className='max-w-md'>
        <DialogHeader>
          <div className='flex items-start gap-3'>
            <ArrowRightLeft className='h-5 w-5 text-primary mt-0.5 flex-shrink-0' />
            <div>
              <DialogTitle>
                {isCentral ? 'Assign Location' : isSingle ? 'Transfer Location' : 'Manage Locations'}
              </DialogTitle>
              <DialogDescription className='mt-1'>
                {user.name}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className='space-y-4 pt-1'>

          {/* ── Central-level user ── */}
          {isCentral && (
            <>
              <div className='flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800'>
                <MapPin className='h-4 w-4 mt-0.5 flex-shrink-0 text-amber-500' />
                <span>
                  This user has <strong>central-level access</strong> with no specific location assigned.
                  Assigning a location will remove that central access.
                </span>
              </div>
              <div className='space-y-2'>
                <p className='text-xs font-medium text-gray-600'>Assign to</p>
                <LocationSelector
                  locationDetails={pickerLocation}
                  setLocationDetails={setPickerLocation}
                  onDeepestChange={setDeepestLocation}
                  layout='column'
                  hideLabels={false}
                />
                <Button
                  type='button'
                  onClick={handleAssign}
                  disabled={!deepestLocation || isBusy}
                  className='w-full'
                >
                  {assignLocationsMutation.isPending
                    ? <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    : <MapPin className='h-4 w-4 mr-2' />
                  }
                  {deepestLocation ? `Assign to "${deepestLocation.name}"` : 'Select a location'}
                </Button>
              </div>
            </>
          )}

          {/* ── Single location: transfer ── */}
          {isSingle && (
            <>
              {/* From → To header */}
              <div className='flex items-center gap-2 text-sm flex-wrap'>
                <span className='text-gray-500 text-xs font-medium'>From</span>
                <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-secondary text-primary'>
                  <MapPin className='h-3 w-3' />
                  {locationIds[0].name}
                </span>
                <ArrowRight className='h-3.5 w-3.5 text-gray-400 flex-shrink-0' />
                <span className='text-gray-500 text-xs font-medium'>To</span>
                {deepestLocation ? (
                  <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-primary/10 text-primary font-medium'>
                    <MapPin className='h-3 w-3' />
                    {deepestLocation.name}
                  </span>
                ) : (
                  <span className='text-xs text-gray-400 italic'>select below</span>
                )}
              </div>

              <LocationSelector
                locationDetails={pickerLocation}
                setLocationDetails={setPickerLocation}
                onDeepestChange={setDeepestLocation}
                layout='column'
                hideLabels={false}
              />

              <Button
                type='button'
                onClick={handleTransfer}
                disabled={!deepestLocation || isBusy}
                className='w-full'
              >
                {isBusy
                  ? <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  : <ArrowRightLeft className='h-4 w-4 mr-2' />
                }
                {deepestLocation ? `Transfer to "${deepestLocation.name}"` : 'Select a destination'}
              </Button>
            </>
          )}

          {/* ── Multiple locations: add / remove ── */}
          {locationIds.length > 1 && (
            <>
              <div>
                <p className='text-xs font-medium text-gray-600 mb-2'>Assigned locations</p>
                <div className='flex flex-wrap gap-1.5'>
                  {locationIds.map(loc => {
                    const isRemoving = removeLocationMutation.isPending &&
                      removeLocationMutation.variables?.geoLocationId === loc.id;
                    return (
                      <span
                        key={loc.id}
                        className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-secondary text-primary'
                      >
                        {loc.name}
                        <button
                          type='button'
                          onClick={() => handleRemove(loc.id)}
                          disabled={isBusy}
                          className='hover:text-red-600 ml-0.5 disabled:opacity-50'
                        >
                          {isRemoving
                            ? <Loader2 className='h-3 w-3 animate-spin' />
                            : <X className='h-3 w-3' />
                          }
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className='rounded-md border border-dashed border-gray-300 p-3 space-y-2 bg-gray-50'>
                <p className='text-xs font-medium text-gray-500'>Add another location</p>
                <LocationSelector
                  locationDetails={pickerLocation}
                  setLocationDetails={setPickerLocation}
                  onDeepestChange={setDeepestLocation}
                  layout='column'
                  hideLabels={false}
                />
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={handleAssign}
                  disabled={
                    !deepestLocation ||
                    locationIds.some(l => l.id === deepestLocation.id) ||
                    isBusy
                  }
                  className='w-full'
                >
                  <Plus className='h-3.5 w-3.5 mr-1' />
                  {deepestLocation ? `Add "${deepestLocation.name}"` : 'Add Location'}
                </Button>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)} disabled={isBusy}>
            {isCentral || isSingle ? 'Cancel' : 'Done'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransferUserLocationDialog;
